import { Badge } from "@/components/ui/badge";
import { Flame, ThermometerSun, Snowflake } from "lucide-react";
import { cn } from "@/lib/utils";

interface LeadScoreBadgeProps {
  score: number;
  size?: "sm" | "md" | "lg";
}

export function LeadScoreBadge({ score, size = "md" }: LeadScoreBadgeProps) {
  const getScoreLevel = (score: number) => {
    if (score >= 75) return { label: "Hot", color: "bg-red-500", icon: Flame };
    if (score >= 50) return { label: "Warm", color: "bg-orange-500", icon: ThermometerSun };
    return { label: "Cold", color: "bg-blue-500", icon: Snowflake };
  };

  const { label, color, icon: Icon } = getScoreLevel(score);
  
  const sizeClasses = {
    sm: "text-[10px] px-1.5 py-0.5 gap-1",
    md: "text-xs px-2 py-1 gap-1.5",
    lg: "text-sm px-3 py-1.5 gap-2",
  };

  const iconSizes = {
    sm: "w-2.5 h-2.5",
    md: "w-3 h-3",
    lg: "w-4 h-4",
  };

  return (
    <Badge 
      variant="secondary" 
      className={cn(
        "font-semibold border-2 transition-all",
        sizeClasses[size],
        color,
        "text-white border-white/20"
      )}
    >
      <Icon className={iconSizes[size]} />
      {score}
      <span className="opacity-80 font-normal">/ {label}</span>
    </Badge>
  );
}

// Calculate AI Lead Score based on multiple factors
export function calculateLeadScore(lead: any): number {
  let score = 0;
  
  // Base score from estimated value (0-30 points)
  if (lead.estimated_value) {
    if (lead.estimated_value >= 50000) score += 30;
    else if (lead.estimated_value >= 25000) score += 20;
    else if (lead.estimated_value >= 10000) score += 10;
  }
  
  // Contact info completeness (0-20 points)
  let contactScore = 0;
  if (lead.contact_email) contactScore += 7;
  if (lead.contact_phone) contactScore += 7;
  if (lead.contact_name) contactScore += 6;
  score += contactScore;
  
  // Source quality (0-15 points)
  const highValueSources = ['referral', 'google', 'direct'];
  if (highValueSources.includes(lead.source)) score += 15;
  else if (lead.source) score += 8;
  
  // Recent activity (0-20 points)
  if (lead.created_at) {
    const daysSinceCreated = Math.floor((Date.now() - new Date(lead.created_at).getTime()) / (1000 * 60 * 60 * 24));
    if (daysSinceCreated <= 1) score += 20;
    else if (daysSinceCreated <= 3) score += 15;
    else if (daysSinceCreated <= 7) score += 10;
    else if (daysSinceCreated <= 14) score += 5;
  }
  
  // Status progression (0-15 points)
  const statusScores: Record<string, number> = {
    'new': 5,
    'contacted': 10,
    'qualified': 15,
    'proposal_sent': 12,
    'negotiating': 13,
  };
  score += statusScores[lead.status || 'new'] || 0;
  
  return Math.min(Math.round(score), 100);
}
